// Is there a newer CAERA than the one this tab is running?
//
// CAERA is a single-page app: once a tab has loaded it keeps running that code
// until someone reloads it, however many deploys go out in between. On
// 2026-09-28 the fix that stops a W2 bundle going out without an offer letter
// was live at 12:25, and a coordinator's tab — open since the morning — sent
// Adrianna Underwood's bundle at 14:42 without one, because it was still on the
// old code. Nothing told her there was a newer version.
//
// Every production build names its bundle by content hash (main.<hash>.js) and
// index.html is served with max-age=0, must-revalidate, so comparing the hash
// this tab loaded with the one index.html names right now is an exact test: they
// differ if and only if a new build has been published since this tab loaded.
//
// The dev server serves an unhashed bundle.js, so there is nothing to compare
// and the check never starts — no false banner while developing.

const HASH = /\/static\/js\/main\.([a-f0-9]+)\.js/;

/** The bundle hash an index.html refers to, or null. */
export function bundleHashFrom(html) {
  const m = typeof html === 'string' ? html.match(HASH) : null;
  return m ? m[1] : null;
}

/** The bundle hash this tab is running, or null outside a production build. */
export function runningBundleHash(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return null;
  for (const s of doc.querySelectorAll('script[src]')) {
    const hash = bundleHashFrom(s.getAttribute('src'));
    if (hash) return hash;
  }
  return null;
}

const CHECK_EVERY_MS = 5 * 60 * 1000;

// Module-level, so the check runs once per tab however often the layout that
// shows the banner remounts — every route change remounts it.
const running = runningBundleHash();
let latest = null;
let started = false;
const listeners = new Set();

async function check() {
  try {
    // A query string as well as no-store: belt and braces against any cache
    // between here and Netlify, which would otherwise hide a new version.
    const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const live = bundleHashFrom(await res.text());
    if (live && live !== running && live !== latest) {
      latest = live;
      listeners.forEach((fn) => fn(latest));
    }
  } catch {
    // Offline or between networks. Try again at the next tick or focus.
  }
}

function start() {
  if (started || !running) return;
  started = true;
  setInterval(check, CHECK_EVERY_MS);
  // Also on coming back to the tab — the moment a long-open tab is most likely
  // to be stale, and the moment someone is about to use it.
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
  window.addEventListener('focus', check);
  check();
}

/**
 * Subscribe to "a newer build is live". Calls back with its hash, at once if
 * one has already been seen. Returns an unsubscribe.
 */
export function onNewVersion(fn) {
  start();
  listeners.add(fn);
  if (latest) fn(latest);
  return () => listeners.delete(fn);
}
